
import React from 'react';
import { Button } from '@/components/ui/button';
import { NavigateFunction } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { cleanSectionContent } from '@/utils/responseFormatUtils';

interface Translation {
  language: string;
  nativeScript: string;
  transliteration: string;
  definition: string;
  note: string;
}

interface KeyUsage {
  reference: string;
  verse: string;
  briefNote: string;
}

interface EtymologyData {
  term: string;
  primaryOriginLanguage: string;
  primaryWordOrPhrase: string;
  translations: Translation[];
  keyUsages: KeyUsage[];
  relatedTerms: string[];
  theologicalInsight: string;
}

export const renderEtymologyContent = (item: any, navigate: NavigateFunction) => {
  if (!item || !item.content) return <p className="bg-white p-4">No content available</p>;

  const etymology: EtymologyData = item.content.etymology;

  const getLanguageColor = (language: string): string => {
    switch (language.toLowerCase()) {
      case "hebrew":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "greek":
        return "bg-purple-50 text-purple-700 border-purple-200";
      case "aramaic":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "latin":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="space-y-6 bg-white p-4">
      <div>
        <h2 className="text-xl md:text-2xl font-playfair font-bold text-heaven-gold mb-2">
          Etymology of "{etymology.term}"
        </h2>
        <p className="text-sm md:text-base text-gray-600 mb-4">
          Primary origin: <span className="font-medium">{etymology.primaryOriginLanguage}</span> - 
          {etymology.primaryWordOrPhrase}
        </p>
      </div>

      {etymology.translations && etymology.translations.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-selah-navy">Translations</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {etymology.translations.map((translation, index) => (
              <Card key={index} className={`border p-4 ${getLanguageColor(translation.language)}`}>
                <h4 className="font-bold mb-2">{translation.language}</h4>
                <p className="text-lg mb-1 font-semibold break-words">{translation.nativeScript}</p>
                <p className="text-sm italic mb-2">Transliteration: {translation.transliteration}</p>
                <p className="text-sm font-medium mb-1">Definition:</p>
                <p className="text-sm mb-2 break-words">{translation.definition}</p>
                {translation.note && (
                  <p className="text-xs opacity-80 break-words">{translation.note}</p>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {etymology.keyUsages && etymology.keyUsages.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-selah-navy">Key Scripture Usages</h3>
          <div className="space-y-4">
            {etymology.keyUsages.map((usage, index) => (
              <div key={index} className="bg-heaven-gold/5 p-4 rounded-lg">
                <p className="font-bold text-heaven-gold">{usage.reference}</p>
                <p className="italic my-1 text-gray-700">"{usage.verse}"</p>
                <p className="text-sm text-gray-600">{usage.briefNote}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {etymology.relatedTerms && etymology.relatedTerms.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-selah-navy">Related Terms</h3>
          <div className="flex flex-wrap gap-2">
            {etymology.relatedTerms.map((term, index) => (
              <span 
                key={index}
                className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
              >
                {term}
              </span>
            ))}
          </div>
        </div>
      )}

      {etymology.theologicalInsight && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-selah-navy">Theological Insight</h3>
          <p className="text-sm text-gray-700 whitespace-pre-line">
            {cleanSectionContent(etymology.theologicalInsight)}
          </p>
        </div>
      )}

      <Button
        onClick={() => navigate(`/etymology`)}
        className="mt-6"
      >
        Go to Etymology Tool
      </Button>
    </div>
  );
};
